'use client';

import { Button, Icon } from '@lulalife/design-system';
import { Calendar } from '@lulalife/design-system/icons/line';
import { useState } from 'react';
import { DSSurface } from './DSSurface';

export type AvailabilityDay = {
  /** The wall date, `YYYY-MM-DD`; it names the row's test id. */
  date: string;
  /** "Mon, Oct 12". */
  dayLabel: string;
  /** One chip each; a day that is open all day has one. */
  windows: string[];
};

const MAX_VISIBLE = 3;

/**
 * The received availability as the frames draw it (Figma 7608:27070): a calendar icon, the day,
 * and each window as an outlined chip. More than three days fold behind "+N more options".
 * The chips only show a window; they take no click. The DS `Chip` is always a button (a screen reader says
 * "button, not pressed" for a time window), and the DS has no read-only pill, so each window is a plain
 * span drawn like the chip's outline.
 */
export const AvailabilityDays = ({ days }: { days: AvailabilityDay[] }) => {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? days : days.slice(0, MAX_VISIBLE);
  const hiddenCount = days.length - MAX_VISIBLE;

  return (
    <DSSurface className="flex flex-col gap-space-8" data-testid="resident-availability.days">
      {visible.map((day) => (
        <div
          key={day.date}
          className="flex flex-wrap items-center gap-space-8"
          data-testid={`resident-availability.date-${day.date}`}
        >
          <Icon size="md" className="text-icon-secondary">
            <Calendar />
          </Icon>
          <span className="desktop-body-md-strong text-text-primary">{day.dayLabel}</span>
          {day.windows.map((window) => (
            <span
              key={window}
              data-testid="resident-availability.window"
              className="inline-flex select-none items-center rounded-full border border-solid border-border-default bg-bg-surface px-space-12 py-space-2 text-desktop-body-sm text-text-primary"
            >
              {window}
            </span>
          ))}
        </div>
      ))}
      {hiddenCount > 0 ? (
        <div>
          <Button type="button" variant="link" onClick={() => setShowAll((prev) => !prev)}>
            {showAll ? 'Show less' : `+${hiddenCount} more options`}
          </Button>
        </div>
      ) : null}
    </DSSurface>
  );
};

export default AvailabilityDays;
